import { applicantProcessService } from "@/services/process";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { formStore } from "@/store/form/form-store";

export const useGetAllApplicantProcesses = () =>
  useQuery<any, Error>({
    queryKey: ["applicant-process"],
    queryFn: applicantProcessService.getAllApplicantProcesses,
  });

export const useCreateApplicantProcess = () => {
  return useMutation<any, Error, any>({
    mutationFn: applicantProcessService.createApplicantProcess,
  });
};

export const useUpdateApplicantProcess = () => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any>({
    mutationFn: applicantProcessService.updateApplicantProcess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-applications"] });
      queryClient.invalidateQueries({ queryKey: ["disabled-applications"] });

      queryClient.invalidateQueries({
        queryKey: ["pending-applications-process"],
      });
      queryClient.invalidateQueries({
        queryKey: ["disabled-applications-process"],
      });
    },
  });
};

export const useDeleteApplicantProcess = () => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, { id: string; reason?: string }>({
    mutationFn: applicantProcessService.deleteApplicantProcess,
    onSuccess: () => {
      // Every application list and count ("pending-applications",
      // "completed-applications-process", ...) may include the deleted row.
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          /-applications(-process)?$/.test(query.queryKey[0]),
      });
      queryClient.invalidateQueries({ queryKey: ["applicant-process"] });
    },
  });
};

export const useGetApplicantProcessByUserId = (userId: string) =>
  useQuery<any, Error, any>({
    queryKey: ["applicant-process", userId],
    queryFn: applicantProcessService.getApplicantProcessByUserId,
  });

export const useBulkCreateApplicantProcess = () => {
  return useMutation<any, Error, FormData>({
    mutationFn: applicantProcessService.bulkCreateApplicantProcess,
  });
};


export const useDownloadApplicantData = () => {
  const { t } = useTranslation();
  const { formList } = formStore();
  return useMutation<any, Error, { processId: string; formId: string }>({
    mutationFn: applicantProcessService.downloadApplicantData,
    onSuccess: (data, variables) => {
      const form = formList.find(f => f.id === variables.formId);
      const formName = form ? form.name : 'Unknown_Form';
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const day = now.getDate().toString().padStart(2, '0');
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      const year = now.getFullYear().toString().slice(-2);
      const dateStr = `${hours}${minutes}-${day}${month}${year}`;
      const fileName = `${formName}_${dateStr}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('common.downloadDataSuccessfully'));
    },
    onError: (error) => {
      console.error('Error downloading data:', error);
      toast.error(t('common.failedToDownloadData'));
    },
  });
};


